import React, { useCallback, useRef } from 'react';
import { TimelineEvent } from './trajectoryTimeline';

interface TimeRangeBrushProps {
  events: TimelineEvent[];
  start: number;
  end: number;
  onChange: (start: number, end: number) => void;
  lineColor: string;
  accentColor: string;
  trackColor: string;
}

const VIEW_W = 760;
const VIEW_H = 46;
const PAD_L = 8;
const PAD_R = 8;

/**
 * The range strip beneath the total line: a whole-history sparkline with a draggable window over
 * it. Dragging picks whichever handle is nearer the pointer, so there is no need to grab a 6px
 * target precisely — the hit area is the entire strip.
 *
 * Keyboard-operable: the handles are real buttons, so arrow keys move the window without a
 * pointer. A brush that only works by dragging is unreachable for keyboard users, and the range
 * is the only way to scope the chart.
 */
const TimeRangeBrush: React.FC<TimeRangeBrushProps> = ({
  events,
  start,
  end,
  onChange,
  lineColor,
  accentColor,
  trackColor,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragging = useRef<'start' | 'end' | null>(null);

  const lastIndex = events.length - 1;
  const plotW = VIEW_W - PAD_L - PAD_R;
  const x = useCallback(
    (i: number) => (lastIndex <= 0 ? PAD_L + plotW / 2 : PAD_L + (i / lastIndex) * plotW),
    [lastIndex, plotW]
  );

  const max = Math.max(1, ...events.map(e => e.total));
  const y = (v: number) => 36 - (v / max) * 26;

  const indexAt = (clientX: number): number => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    const frac = (clientX - rect.left) / rect.width;
    const plotFrac = (frac * VIEW_W - PAD_L) / plotW;
    return Math.max(0, Math.min(lastIndex, Math.round(plotFrac * lastIndex)));
  };

  const applyDrag = (clientX: number) => {
    if (!dragging.current) return;
    const i = indexAt(clientX);
    if (dragging.current === 'start') onChange(Math.min(i, end - 1), end);
    else onChange(start, Math.max(i, start + 1));
  };

  const handlePointerDown = (ev: React.PointerEvent<SVGSVGElement>) => {
    if (lastIndex < 1) return;
    const i = indexAt(ev.clientX);
    dragging.current = Math.abs(i - start) <= Math.abs(i - end) ? 'start' : 'end';
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    applyDrag(ev.clientX);
  };

  const handlePointerMove = (ev: React.PointerEvent<SVGSVGElement>) => {
    if (dragging.current) applyDrag(ev.clientX);
  };

  const stopDrag = () => {
    dragging.current = null;
  };

  const nudge = (which: 'start' | 'end', delta: number) => {
    if (which === 'start') onChange(Math.max(0, Math.min(start + delta, end - 1)), end);
    else onChange(start, Math.min(lastIndex, Math.max(end + delta, start + 1)));
  };

  const onHandleKey = (which: 'start' | 'end') => (ev: React.KeyboardEvent) => {
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      nudge(which, -1);
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      nudge(which, 1);
    }
  };

  let spark = '';
  events.forEach((e, i) => {
    spark += (i === 0 ? 'M ' : ' L ') + x(i) + ' ' + y(e.total);
  });

  const x0 = x(start);
  const x1 = x(end);

  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        data-testid="range-brush"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        style={{ display: 'block', width: '100%', height: 'auto', touchAction: 'none', cursor: 'ew-resize' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        role="img"
        aria-label="Time range selector"
      >
        <rect x={PAD_L} y={6} width={plotW} height={30} rx={4} fill={trackColor} />
        <path d={spark} fill="none" stroke={lineColor} strokeWidth={1.5} opacity={0.5} />
        <rect
          x={x0}
          y={6}
          width={Math.max(2, x1 - x0)}
          height={30}
          fill={accentColor}
          fillOpacity={0.16}
          stroke={accentColor}
          strokeWidth={1}
        />
        <rect x={x0 - 3} y={2} width={6} height={38} rx={3} fill={accentColor} />
        <rect x={x1 - 3} y={2} width={6} height={38} rx={3} fill={accentColor} />
      </svg>

      {/* Keyboard equivalents for the two handles, visually hidden but focusable. */}
      <button
        type="button"
        aria-label={`Range start: ${events[start]?.label ?? ''}. Use arrow keys to adjust.`}
        onKeyDown={onHandleKey('start')}
        style={SR_ONLY}
      />
      <button
        type="button"
        aria-label={`Range end: ${events[end]?.label ?? ''}. Use arrow keys to adjust.`}
        onKeyDown={onHandleKey('end')}
        style={SR_ONLY}
      />
    </div>
  );
};

const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default TimeRangeBrush;
